import React, { useRef } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  Buildings,
  ClockCounterClockwise,
  DotsThreeVertical,
  Download,
  FileArrowDown,
  FileArrowUp,
  Info,
  MagnifyingGlass,
  PencilSimple,
  User,
  WarningCircle,
} from 'phosphor-react';

import { GlassCard } from '../../../../components/glass';
import { IContratoSocietario, TipoOperacaoSocietario } from '../../../../hooks/useSocietario';
import { computeProximoStep, WIZARD_STEP_LABELS } from '../criar/wizardProgress';
import { TIPO_ALTERACAO_OPTIONS } from '../criar/schema';
import { RelatorioFidelidadeDrawer } from '../../auditoria/components/RelatorioFidelidadeDrawer';
import {
  AcaoPrincipal,
  cnpjParaExibir,
  estadoDoCard,
  formatarDataHora,
  houveAtualizacao,
  ROTULOS_DA_ACAO,
  situacaoDoCard,
  tempoRelativo,
  ultimaAtividade,
} from '../listagem';

interface ContratoSocietarioCardProps {
  contrato: IContratoSocietario;
  /** `original: true` força o DOCX gerado, ignorando a versão revisada. */
  onDownload: (id: number, opcoes?: { original?: boolean }) => void;
  /** Reenvia o DOCX ajustado à mão; passa a ser o documento do ato. */
  onEnviarRevisado?: (id: number, file: File) => void;
  /** Baixa o contrato VIGENTE anexado (input do cliente em alterações). */
  onDownloadVigente?: (id: number) => void;
  /** Arquiva (status 'arquivado'); a página confirma antes. */
  onRemover: (contrato: IContratoSocietario) => void;
  /** Reabre o contrato no wizard (rascunho, ou gerado/em revisão no v3 para regerar). */
  onContinuarEdicao?: (contrato: IContratoSocietario) => void;
  onClick?: (contrato: IContratoSocietario) => void;
  /** Realça a borda do card (alvo do deep-link `?destaque=` vindo da legalização). */
  destacado?: boolean;
}

const tipoOperacaoLabels: Record<TipoOperacaoSocietario, string> = {
  constituicao: 'Constituição',
  alteracao: 'Alteração',
  transformacao: 'Transformação',
};

const ROTULO_DO_TIPO_DE_ALTERACAO = new Map<string, string>(
  TIPO_ALTERACAO_OPTIONS.map(o => [o.value, o.label]),
);
const ORDEM_DO_TIPO_DE_ALTERACAO = new Map<string, number>(
  TIPO_ALTERACAO_OPTIONS.map((o, i) => [o.value, i]),
);

// Tint pelo significado: ambar = alguém precisa agir; azul = pronto; verde = assinado.
const TINT_PRECISA_DE_ACAO = '#F59E0B';
const TINT_POR_STATUS: Partial<Record<IContratoSocietario['status'], string>> = {
  gerado: '#1B88F5',
  assinado: '#22C55E',
};

// O tema do admin fixa a cor de `solid`/`outline` (sempre azul da marca): por isso
// `ghost` com cor explícita.
const ESTILO_DA_ACAO: Record<AcaoPrincipal, Record<string, any>> = {
  continuar: {
    bg: 'primary',
    color: 'white',
    _hover: { bg: 'blue.600' },
    _active: { bg: 'blue.700' },
  },
  regerar: {
    bg: 'orange.500',
    color: 'white',
    _hover: { bg: 'orange.600' },
    _active: { bg: 'orange.700' },
  },
  revisar_vistoria: {
    bg: 'orange.500',
    color: 'white',
    _hover: { bg: 'orange.600' },
    _active: { bg: 'orange.700' },
  },
  baixar: {
    bg: 'blue.50',
    color: 'blue.700',
    _hover: { bg: 'blue.100' },
    _active: { bg: 'blue.200' },
  },
};

const badgeProps = {
  fontSize: '9px',
  borderRadius: 'full',
  px: 2,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
};

export const ContratoSocietarioCard: React.FC<ContratoSocietarioCardProps> = ({
  contrato,
  onDownload,
  onEnviarRevisado,
  onDownloadVigente,
  onRemover,
  onContinuarEdicao,
  onClick,
  destacado = false,
}) => {
  const estado = estadoDoCard(contrato);
  const { podeBaixar, podeAbrirNoWizard, podeVerVistoria } = estado;
  const isAlteracao = contrato.tipo_operacao === 'alteracao';
  const situacao = situacaoDoCard(contrato, estado);

  // Ação que abre o wizard só existe se a página passou o callback.
  const abreWizard = (a: AcaoPrincipal | null) => a === 'continuar' || a === 'regerar';
  const acao = abreWizard(estado.acaoPrincipal) && !onContinuarEdicao ? null : estado.acaoPrincipal;

  // Versão revisada: o DOCX que o operador reenviou depois de ajustar no Word. Ela
  // vira o documento do ato; o gerado continua no menu (é o par que permite o diff).
  const temRevisada = !!contrato.contrato_editado_s3_key;
  // Enviar revisada tem o mesmo gate do download: só se revisa o que se baixou.
  const podeEnviarRevisado = isAlteracao && podeBaixar && !!onEnviarRevisado;
  const podeBaixarVigente =
    isAlteracao && !!contrato.contrato_vigente_s3_key && !!onDownloadVigente;
  const podeArquivar = contrato.status !== 'arquivado';
  const inputRevisado = useRef<HTMLInputElement | null>(null);
  const idInputRevisado = `revisado-${contrato.id}`;

  // Vistoria da IA (Relatório de Fidelidade): quem vê o card vê a vistoria dele. O
  // drawer trata "sem relatório" (gerado antes do auditor).
  const fidelidade = useDisclosure();
  const queryClient = useQueryClient();

  const tipos = (contrato.tipos_alteracao ?? [])
    .slice()
    .sort(
      (a, b) =>
        (ORDEM_DO_TIPO_DE_ALTERACAO.get(a) ?? 99) - (ORDEM_DO_TIPO_DE_ALTERACAO.get(b) ?? 99),
    )
    .map(t => ROTULO_DO_TIPO_DE_ALTERACAO.get(t) ?? t);
  const numSocios = contrato.socios?.length ?? 0;
  const rotuloDoTipo =
    isAlteracao && contrato.numero_alteracao
      ? `${contrato.numero_alteracao}ª alteração`
      : tipoOperacaoLabels[contrato.tipo_operacao];

  // O que falta, em uma linha. A vistoria pendente já tem selo e botão; aqui fica o
  // que o rótulo sozinho não explica.
  const proximoStep = podeAbrirNoWizard ? computeProximoStep(contrato) : null;
  const proximaEtapa =
    proximoStep && proximoStep !== 'gerado' ? WIZARD_STEP_LABELS[proximoStep] : null;
  let aviso: { texto: string; atencao: boolean } | null = null;
  if (contrato.status === 'em_revisao') {
    aviso = { texto: 'Editado depois de gerar: gere de novo', atencao: true };
  } else if (acao === 'continuar' && proximaEtapa) {
    aviso = { texto: `Próxima etapa: ${proximaEtapa}`, atencao: false };
  }

  // So' o tempo ("há 54 min"): ao lado do botao, "atualizado há..." era cortado. O
  // tooltip diz se foi criacao ou salvamento. Conta o salvamento de socio tambem.
  const ultima = ultimaAtividade(contrato);
  const salvo = houveAtualizacao(contrato.dt_criacao, ultima);
  const quando = tempoRelativo(salvo ? ultima : contrato.dt_criacao);
  const rotuloDasDatas = [
    `Criado em ${formatarDataHora(contrato.dt_criacao)}`,
    salvo ? `salvo em ${formatarDataHora(ultima)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const rotuloDaRevisada = `Documento ajustado à mão${
    contrato.contrato_editado_por_nome ? ` por ${contrato.contrato_editado_por_nome}` : ''
  }${
    contrato.contrato_editado_em ? ` em ${formatarDataHora(contrato.contrato_editado_em)}` : ''
  }. É ele que vale.`;

  const tint = estado.precisaDeAcao ? TINT_PRECISA_DE_ACAO : TINT_POR_STATUS[contrato.status];

  const executarAcao = () => {
    if (acao === 'continuar' || acao === 'regerar') onContinuarEdicao?.(contrato);
    else if (acao === 'revisar_vistoria') fidelidade.onOpen();
    else if (acao === 'baixar') onDownload(contrato.id);
  };

  const mostrarAbrirNoWizard = podeAbrirNoWizard && !!onContinuarEdicao && !abreWizard(acao);
  const mostrarVistoriaNoMenu = podeVerVistoria && acao !== 'revisar_vistoria';
  const temBaixar = podeBaixar || podeBaixarVigente;
  const temContrato = mostrarAbrirNoWizard || mostrarVistoriaNoMenu;
  const temMenu = temBaixar || podeEnviarRevisado || temContrato || podeArquivar;

  return (
    <GlassCard
      intensity="medium"
      p={5}
      preencherAltura
      tint={tint}
      // Sem `hoverable` (a pedido da operação, 27/08): o card subia no hover e a
      // fileira balançava enquanto o operador mirava um botão.
      destacado={destacado}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick ? () => onClick(contrato) : undefined}
    >
      <Flex direction="column" h="100%" gap={3}>
        {/* Cabeçalho: número e tipo à esquerda; situação à direita */}
        <Flex justify="space-between" align="center" gap={2}>
          <Flex align="center" gap={2} minW={0}>
            <Text fontSize="sm" fontWeight="700" color="gray.700">
              #{contrato.id}
            </Text>
            <Badge colorScheme={isAlteracao ? 'purple' : 'blue'} {...badgeProps}>
              {rotuloDoTipo}
            </Badge>
          </Flex>
          <Flex gap={1.5} flexWrap="wrap" justify="flex-end">
            {temRevisada && (
              <Tooltip label={rotuloDaRevisada} placement="top" fontSize="xs">
                <Badge colorScheme="green" {...badgeProps}>
                  Revisado
                </Badge>
              </Tooltip>
            )}
            <Tooltip
              label={
                situacao.solido ? 'Alguém precisa conferir a vistoria da IA antes do download' : ''
              }
              isDisabled={!situacao.solido}
              placement="top"
              fontSize="xs"
            >
              <Badge
                colorScheme={situacao.cor}
                variant={situacao.solido ? 'solid' : 'subtle'}
                {...badgeProps}
              >
                {situacao.rotulo}
              </Badge>
            </Tooltip>
          </Flex>
        </Flex>

        {/* Empresa */}
        <Box>
          <Flex align="flex-start" gap={1.5}>
            <Box pt="3px" flexShrink={0}>
              <Buildings size={14} color="#475569" weight="duotone" />
            </Box>
            {contrato.razao_social ? (
              <Text
                fontWeight="700"
                fontSize="sm"
                color="#0F172A"
                noOfLines={2}
                letterSpacing="-0.01em"
              >
                {contrato.razao_social}
              </Text>
            ) : (
              <Text fontSize="sm" fontStyle="italic" color="gray.500">
                Empresa ainda não informada
              </Text>
            )}
          </Flex>
          {(contrato.cnpj || numSocios > 0) && (
            <Text fontSize="xs" color="gray.500" ml="21px" mt={0.5}>
              {[
                contrato.cnpj ? `CNPJ ${cnpjParaExibir(contrato.cnpj)}` : null,
                numSocios > 0 ? `${numSocios} ${numSocios === 1 ? 'sócio' : 'sócios'}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          )}
        </Box>

        {/* O que a alteração muda */}
        {tipos.length > 0 && (
          <Flex gap={1} flexWrap="wrap" aria-label="Tipos de alteração">
            {tipos.slice(0, 3).map(t => (
              <Tag key={t} size="sm" variant="subtle" colorScheme="purple" borderRadius="full">
                {t}
              </Tag>
            ))}
            {tipos.length > 3 && (
              <Tooltip label={tipos.slice(3).join(', ')} placement="top" fontSize="xs">
                <Tag size="sm" variant="subtle" colorScheme="gray" borderRadius="full">
                  +{tipos.length - 3}
                </Tag>
              </Tooltip>
            )}
          </Flex>
        )}

        {aviso && (
          <Flex
            align="center"
            gap={2}
            px={3}
            py={1.5}
            borderRadius="lg"
            bg={aviso.atencao ? 'orange.50' : 'blue.50'}
            color={aviso.atencao ? 'orange.800' : 'blue.700'}
            fontSize="xs"
            fontWeight="600"
          >
            {aviso.atencao ? (
              <WarningCircle size={14} weight="bold" />
            ) : (
              <Info size={14} weight="bold" />
            )}
            <Text>{aviso.texto}</Text>
          </Flex>
        )}

        {/* Rodapé: quem e quando; ação principal e o menu */}
        <Flex
          mt="auto"
          pt={3}
          borderTop="1px solid"
          borderColor="rgba(15, 23, 42, 0.06)"
          align="center"
          justify="space-between"
          gap={2}
        >
          <Box minW={0} fontSize="xs" color="gray.600">
            {contrato.criado_por_nome && (
              <Tooltip
                label={`Criado por ${contrato.criado_por_nome}`}
                placement="top"
                fontSize="xs"
              >
                <Flex align="center" gap={1} minW={0}>
                  <Box flexShrink={0}>
                    <User size={12} />
                  </Box>
                  <Text noOfLines={1}>{contrato.criado_por_nome}</Text>
                </Flex>
              </Tooltip>
            )}
            <Tooltip label={rotuloDasDatas} placement="top" fontSize="xs">
              <Flex align="center" gap={1}>
                <Box flexShrink={0}>
                  <ClockCounterClockwise size={12} />
                </Box>
                <Text noOfLines={1}>{quando}</Text>
              </Flex>
            </Tooltip>
          </Box>

          <Flex align="center" gap={1} flexShrink={0}>
            {acao && (
              <Button
                variant="ghost"
                size="sm"
                h="32px"
                px={3}
                fontSize="sm"
                fontWeight="600"
                {...ESTILO_DA_ACAO[acao]}
                onClick={e => {
                  e.stopPropagation();
                  executarAcao();
                }}
              >
                {ROTULOS_DA_ACAO[acao]}
              </Button>
            )}
            {temMenu && (
              <Menu placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  aria-label="Mais ações"
                  icon={<DotsThreeVertical size={18} weight="bold" />}
                  variant="ghost"
                  size="sm"
                  h="32px"
                  minW="32px"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
                {/* ⚠️ Portal OBRIGATÓRIO: o GlassCard tem `overflow: hidden` e
                    recortaria o menu. O zIndex também: o GlassCard monta
                    contexto de empilhamento próprio e o menu abriria atrás do
                    vidro do card vizinho. */}
                <Portal>
                  <MenuList
                    fontSize="sm"
                    minW="270px"
                    zIndex="popover"
                    onClick={e => e.stopPropagation()}
                  >
                    {/* Agrupado por VERBO: a dúvida é "o que eu vou receber", e
                        com duas versões do ato o `(atual)` diz qual vale. */}
                    {temBaixar && (
                      <MenuGroup title="Baixar" fontSize="xs" color="gray.500">
                        {podeBaixar && temRevisada && (
                          <MenuItem
                            icon={<Download size={14} weight="bold" />}
                            onClick={() => onDownload(contrato.id)}
                          >
                            Versão revisada (atual)
                          </MenuItem>
                        )}
                        {podeBaixar && (
                          <MenuItem
                            icon={<Download size={14} weight="bold" />}
                            onClick={() =>
                              onDownload(contrato.id, temRevisada ? { original: true } : undefined)
                            }
                          >
                            {temRevisada
                              ? 'Original gerado pelo sistema'
                              : 'Contrato gerado (atual)'}
                          </MenuItem>
                        )}
                        {podeBaixarVigente && (
                          <MenuItem
                            icon={<FileArrowDown size={14} weight="bold" />}
                            onClick={() => onDownloadVigente!(contrato.id)}
                          >
                            Contrato vigente (anexado)
                          </MenuItem>
                        )}
                      </MenuGroup>
                    )}
                    {podeEnviarRevisado && (
                      <>
                        {temBaixar && <MenuDivider />}
                        <MenuGroup title="Enviar" fontSize="xs" color="gray.500">
                          {/* ⚠️ `as="label"`: com `input.click()` no onClick o
                              seletor pode não abrir (o menu fecha e devolve o
                              foco antes). Um <label> abre nativamente. */}
                          <MenuItem
                            as="label"
                            htmlFor={idInputRevisado}
                            cursor="pointer"
                            icon={<FileArrowUp size={14} weight="bold" />}
                          >
                            {temRevisada ? 'Substituir versão revisada' : 'Versão revisada (DOCX)'}
                          </MenuItem>
                        </MenuGroup>
                      </>
                    )}
                    {temContrato && (
                      <>
                        {(temBaixar || podeEnviarRevisado) && <MenuDivider />}
                        <MenuGroup title="Contrato" fontSize="xs" color="gray.500">
                          {mostrarAbrirNoWizard && (
                            <MenuItem
                              icon={<PencilSimple size={14} weight="bold" />}
                              onClick={() => onContinuarEdicao!(contrato)}
                            >
                              Abrir no wizard (regerar com ajustes)
                            </MenuItem>
                          )}
                          {mostrarVistoriaNoMenu && (
                            <MenuItem
                              icon={<MagnifyingGlass size={14} weight="bold" />}
                              onClick={fidelidade.onOpen}
                            >
                              <Box>
                                <Text>Vistoria de fidelidade</Text>
                                {contrato.auditoria_revisada_em && (
                                  <Text fontSize="xs" color="gray.500">
                                    Revisada
                                    {contrato.auditoria_revisada_por_nome
                                      ? ` por ${contrato.auditoria_revisada_por_nome}`
                                      : ''}{' '}
                                    em {formatarDataHora(contrato.auditoria_revisada_em)}
                                  </Text>
                                )}
                              </Box>
                            </MenuItem>
                          )}
                        </MenuGroup>
                      </>
                    )}
                    {podeArquivar && (
                      <>
                        {(temBaixar || podeEnviarRevisado || temContrato) && <MenuDivider />}
                        <MenuItem
                          icon={<Archive size={14} weight="bold" />}
                          onClick={() => onRemover(contrato)}
                        >
                          Arquivar
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </Portal>
              </Menu>
            )}
          </Flex>
        </Flex>
      </Flex>
      {podeEnviarRevisado && (
        <input
          id={idInputRevisado}
          ref={inputRevisado}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: 'none' }}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            const file = e.target.files?.[0];
            // Zera o value: sem isso, reenviar o MESMO arquivo corrigido não
            // dispara `onChange` e o operador acha que o upload falhou.
            e.target.value = '';
            if (file) onEnviarRevisado!(contrato.id, file);
          }}
        />
      )}
      <RelatorioFidelidadeDrawer
        contratoId={contrato.id}
        revisadaId={contrato.auditoria_revisada_id ?? null}
        isOpen={fidelidade.isOpen}
        onClose={fidelidade.onClose}
        onRevisado={() => queryClient.invalidateQueries({ queryKey: ['societario-contratos'] })}
      />
    </GlassCard>
  );
};

export default ContratoSocietarioCard;
